import AIDrawerSection from "@/admin/ai/AIDrawerSection"
import { GlobalDrawerParams, useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import { LabelDataWithLowercaseOptions, useLabel } from "@/core/context/LabelsContext"
import CommunityDescriptionImage from "@assets/images/ai-utility/community_description.png"
import ImageGeneratorImage from "@assets/images/ai-utility/image_generator.png"
import QuizGeneratorImage from "@assets/images/ai-utility/quiz_generator.png"
import SuggestionsImage from "@assets/images/ai-utility/suggestions.png"
import TimeEstimatesImage from "@assets/images/ai-utility/time_estimates.png"
import VideoImage from "@assets/images/ai-utility/video.png"
import WritewithAIImage from "@assets/images/ai-utility/write_with_ai.png"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoDrawer, DiscoDrawerHeader, DiscoLink, DiscoText } from "@disco-ui"
import DiscoImage from "@disco-ui/image/DiscoImage"
import { QueryParamTab } from "@disco-ui/tabs/DiscoQueryParamTabs"

const sections: (
  organizationLabel: LabelDataWithLowercaseOptions
) => { key: string; title: string; description: string; image: string }[] = (
  organizationLabel
) => [
  {
    key: "Image Generator",
    title: "Image Generator",
    description: "Create custom images from any description.",
    image: ImageGeneratorImage,
  },
  {
    key: "Quiz Generator",
    title: "Quiz Generator",
    description: "Builds quizzes from your lesson and module content.",
    image: QuizGeneratorImage,
  },
  {
    key: "Write with AI",
    title: "Write with AI",
    description: "Generate content from a simple prompt to jumpstart your writing.",
    image: WritewithAIImage,
  },
  {
    key: "Time Estimates",
    title: "Time Estimates",
    description: "Estimates how long it takes to complete a lesson based on its content.",
    image: TimeEstimatesImage,
  },
  {
    key: "Video Transcription, Chapters & Summaries",
    title: "Video Transcription, Chapters & Summaries",
    description: "Transcribes videos, breaks them into chapters & generates summaries.",
    image: VideoImage,
  },
  {
    key: "Suggestion Actions from Reports",
    title: "Suggestion Actions from Reports",
    description: "Recommended next steps based on learner activity and report data.",
    image: SuggestionsImage,
  },
  {
    key: "Community Description",
    title: `${organizationLabel.singular} Description`,
    description: `Generate a ${organizationLabel.singularLowercase} description based on your ${organizationLabel.singularLowercase}'s data.`,
    image: CommunityDescriptionImage,
  },
]

export default function AIUtilityDrawer() {
  const drawer = useGlobalDrawer("aiUtilities")
  const classes = useStyles()
  const organizationLabel = useLabel("organization")

  const tabs: QueryParamTab<GlobalDrawerParams<"aiUtilities">>[] = [
    {
      label: "Details",
      params: { tab: "details" },
      testid: "details",
    },
  ]

  return (
    <DiscoDrawer
      open={drawer.isOpen}
      onClose={drawer.close}
      size={"lg"}
      containerClasses={{
        drawerContainer: classes.drawer,
      }}
    >
      <DiscoDrawerHeader
        title={<DiscoText variant={"heading-sm-600"}>{"Utilities"}</DiscoText>}
        onClose={drawer.close}
        className={classes.drawerHeader}
        classes={{
          header: classes.headerTitle,
        }}
        queryParamTabs={tabs}
        disableExpand
      />

      {drawer.isOpen && (
        <>
          <AIDrawerSection
            textVariant={"body-md-600"}
            growLabel
            sections={sections(organizationLabel).map((section) => ({
              key: section.key,
              variant: "one-column",
              title: section.title,
              description: section.description,
              rightContent: <DiscoImage src={section.image} width={170} />,
            }))}
          />

          <DiscoText marginTop={3} color={"groovy.neutral.500"}>
            {"Discover how Disco's AI tools can streamline your workflows. "}
            <DiscoLink
              to={
                "https://support.disco.co/hc/en-us/articles/38785597314196-Disco-AI-Utilities"
              }
              target={"_blank"}
            >
              {"Learn More"}
            </DiscoLink>
          </DiscoText>
        </>
      )}
    </DiscoDrawer>
  )
}

const useStyles = makeUseStyles((theme) => ({
  drawerHeader: {
    padding: theme.spacing(2.5, 2.5, 0),
  },
  drawer: {
    padding: theme.spacing(3),
  },
  headerTitle: {
    paddingBottom: 0,
  },
}))
